import { ParagraphOverrides } from '@/api/circuits.api';
import { CircuitFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { SupportedLocale } from '@/types/supported-locale.type';
import {
  Control,
  get,
  useFormContext,
  useFormState,
  useWatch,
} from 'react-hook-form';

const CONFIG_OVERRIDE_FIELDS = [
  { overrideKey: 'summary', translationKey: 'summary', formKey: 'summary' },
  {
    overrideKey: 'importantPoints',
    translationKey: 'importantPoints',
    formKey: 'importantPoints',
  },
  {
    overrideKey: 'priceIncludes',
    translationKey: 'priceIncludes',
    formKey: 'priceIncludes',
  },
  {
    overrideKey: 'priceExcludes',
    translationKey: 'priceExcludes',
    formKey: 'priceExcludes',
  },
  {
    overrideKey: 'modificationsConditions',
    translationKey: 'modificationConditions',
    formKey: 'modificationsConditions',
  },
  {
    overrideKey: 'cancellationConditions',
    translationKey: 'cancellationConditions',
    formKey: 'cancellationConditions',
  },
] as const;

const readTranslatableField = (
  values: CircuitFormDataWithTranslations | undefined,
  language: SupportedLocale | undefined,
  translationKey: string,
  formKey: string,
) => {
  if (!values) return undefined;
  if (language) {
    const translated = get(
      values,
      `translations.${language}.${translationKey}`,
    );
    if (translated !== undefined) return translated;
  }
  return get(values, formKey);
};

const readDayField = (
  values: CircuitFormDataWithTranslations | undefined,
  language: SupportedLocale | undefined,
  dayIndex: number,
  kind: 'stepTitle' | 'description',
) => {
  if (!values) return undefined;
  if (language) {
    const translated = get(
      values,
      `translations.${language}.circuitDays.${dayIndex}.${kind}`,
    );
    if (translated !== undefined) return translated;
  }
  return get(values, `circuitDays.${dayIndex}.${kind}`);
};

const isDirtyFlagSet = (value: unknown): boolean => {
  if (value === true) return true;
  if (value && typeof value === 'object') {
    return Object.values(value).some(isDirtyFlagSet);
  }
  return false;
};

export const collectEditedParagraphOverrideKeys = ({
  values,
  baseline,
  language,
  paragraphOverrides,
}: {
  values: CircuitFormDataWithTranslations;
  baseline: CircuitFormDataWithTranslations | undefined;
  language: SupportedLocale;
  paragraphOverrides?: ParagraphOverrides;
}): string[] => {
  if (!paragraphOverrides || !baseline) return [];

  const editedKeys: string[] = [];

  for (const {
    overrideKey,
    translationKey,
    formKey,
  } of CONFIG_OVERRIDE_FIELDS) {
    if (!paragraphOverrides[overrideKey]?.length) continue;
    const current = readTranslatableField(
      values,
      language,
      translationKey,
      formKey,
    );
    const saved = readTranslatableField(
      baseline,
      language,
      translationKey,
      formKey,
    );
    if (current !== saved) editedKeys.push(overrideKey);
  }

  for (const key of Object.keys(paragraphOverrides)) {
    const match = key.match(/^day(Title|Description)_(\d+)$/);
    if (!match) continue;
    const kind = match[1] === 'Title' ? 'stepTitle' : 'description';
    const dayIndex = parseInt(match[2], 10);
    const current = readDayField(values, language, dayIndex, kind);
    const saved = readDayField(baseline, language, dayIndex, kind);
    if (current !== saved) editedKeys.push(key);
  }

  return editedKeys;
};

export const useParagraphOverrideHighlight = ({
  paragraphOverrides,
  language,
  control,
}: {
  paragraphOverrides?: ParagraphOverrides;
  language: SupportedLocale;
  control: Control<CircuitFormDataWithTranslations>;
  suppressedOverrideFields?: Set<string>;
}) => {
  const { getValues } = useFormContext<CircuitFormDataWithTranslations>();
  const { dirtyFields, defaultValues } = useFormState({ control });

  // Must consume watched values in render — otherwise RHF won't re-render
  // this component when the user types in a translatable paragraph field.
  const watchedValues = useWatch({ control }) as
    | CircuitFormDataWithTranslations
    | undefined;
  const watchedLocaleTranslations = useWatch({
    control,
    name: `translations.${language}`,
  });
  const watchedCircuitDays = useWatch({ control, name: 'circuitDays' });

  const baseValues = watchedValues ?? getValues();
  const formValues: CircuitFormDataWithTranslations = {
    ...baseValues,
    ...(watchedCircuitDays ? { circuitDays: watchedCircuitDays } : {}),
    ...(watchedLocaleTranslations
      ? {
          translations: {
            ...baseValues.translations,
            [language]: watchedLocaleTranslations,
          },
        }
      : {}),
  };
  const baseline = defaultValues as CircuitFormDataWithTranslations | undefined;

  const isConfigFieldEdited = (field: string) => {
    const configField = CONFIG_OVERRIDE_FIELDS.find(
      (entry) => entry.overrideKey === field,
    );
    const translationKey = configField?.translationKey ?? field;
    const formKey = configField?.formKey ?? field;

    const dirty = dirtyFields as Record<string, unknown> & {
      translations?: Record<string, Record<string, unknown>>;
    };

    if (
      isDirtyFlagSet(dirty[formKey]) ||
      (language &&
        isDirtyFlagSet(dirty.translations?.[language]?.[translationKey]))
    ) {
      return true;
    }

    const current = readTranslatableField(
      formValues,
      language,
      translationKey,
      formKey,
    );
    const saved = readTranslatableField(
      baseline,
      language,
      translationKey,
      formKey,
    );

    return current !== saved;
  };

  const isDayFieldEdited = (
    targetDayIndex: number,
    kind: 'stepTitle' | 'description',
  ) => {
    const dirty = dirtyFields as {
      circuitDays?: Array<{ stepTitle?: boolean; description?: boolean }>;
      translations?: Record<
        string,
        {
          circuitDays?: Array<{
            stepTitle?: boolean;
            description?: boolean;
          }>;
        }
      >;
    };

    if (
      isDirtyFlagSet(dirty.circuitDays?.[targetDayIndex]?.[kind]) ||
      (language &&
        isDirtyFlagSet(
          dirty.translations?.[language]?.circuitDays?.[targetDayIndex]?.[kind],
        ))
    ) {
      return true;
    }

    const current = readDayField(formValues, language, targetDayIndex, kind);
    const saved = readDayField(baseline, language, targetDayIndex, kind);

    return current !== saved;
  };

  const hasOverride = (field: string) =>
    Boolean(paragraphOverrides?.[field]?.length);

  const hasDayOverride = (key: string) =>
    Boolean(paragraphOverrides?.[key]?.length);

  return {
    hasOverride,
    hasDayOverride,
    isConfigFieldEdited,
    isDayFieldEdited,
  };
};
